import { useEffect, useState } from "react";
import { Route, Redirect, Switch } from "react-router-dom";
import { IonApp, setupIonicReact } from "@ionic/react";
import { Location } from "history";
import { IonReactRouter } from "@ionic/react-router";
import { IntlProvider } from "react-intl";
import config from "./config";
import { useQuery } from "@tanstack/react-query";
import { collection, getDocs } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";

import { deserialize } from "./utils/serialize";
import serialize from "./utils/serialize";

import { SocketProvider } from "./providers/socket";
import { NotificationProvider } from "./providers/desktop-notification";
import { PushNotificationProvider } from "./providers/native-push-notification";

// Providers

import { useAuth } from "./providers/auth";

// Pages
import HOCAuth from "./pages/auth/HOCAuth";
import HOCDashboard from "./pages/dashboard/HOCDashboard";

// Components
import WebChat from "./components/web-chat";
import GoogleAnalytics from "./components/GoogleAnalytics";
import Maintenance from "./pages/statics/Maintenance";

/* Core CSS required for Ionic components to work properly */
import "@ionic/react/css/core.css";

/* Basic CSS for apps built with Ionic */
import "@ionic/react/css/normalize.css";
import "@ionic/react/css/structure.css";
import "@ionic/react/css/typography.css";

/* Optional CSS utils that can be commented out */
import "@ionic/react/css/padding.css";
import "@ionic/react/css/float-elements.css";
import "@ionic/react/css/text-alignment.css";
import "@ionic/react/css/text-transformation.css";
import "@ionic/react/css/flex-utils.css";
import "@ionic/react/css/display.css";

/* Languages */
import es from "./lang/es";
import en from "./lang/en";
import pt from "./lang/pt";

/* old good bootstrap */
import "bootstrap/dist/css/bootstrap.min.css";

/* Theme variables */
import "./theme/variables.css";

/* Custom css */
import "./theme/custom.css";
import { useAppStore } from "./store/app";
import { db } from "./lib/firebase";

setupIonicReact();

const isNative = Capacitor.isNativePlatform();
const version = config.VERSION;

function loadLocaleData(locale: string) {
  switch (locale) {
    case "es":
      return es;
    case "pt":
      return pt;
    default:
      return en;
  }
}

const App: React.FC = () => {
  const [showUpdateAlert, setShowUpdateAlert] = useState(false);
  const { refreshUser } = useAuth();
  const loading = useAppStore((state) => state.loading);
  const user = useAppStore((state) => state.user);
  const { language } = user;
  const messages = loadLocaleData(language);

  const getAppStatus = async () => {
    const snapshot = await getDocs(collection(db, "app"));
    const appStaus = snapshot.docs[0]?.data() || null;
    return appStaus as {
      appVersion: string;
      forceUpdate: boolean;
      isMaintenanceMode: boolean;
    } | null;
  };

  const appQuery = useQuery({
    queryKey: ["appStatus"],
    queryFn: getAppStatus,
    refetchOnWindowFocus: (query) => !!query.state.data?.isMaintenanceMode,
    refetchInterval: (query) => (query?.isMaintenanceMode ? 60_000 : false),
  });

  const isMaintenance = !!appQuery.data?.isMaintenanceMode;
  const {
    appVersion: remoteVersion,
    forceUpdate,
    isMaintenanceMode,
  } = appQuery.data || {};

  useEffect(() => {
    if (
      isNative &&
      !isMaintenanceMode &&
      forceUpdate &&
      version.vIdentifier &&
      remoteVersion &&
      !appQuery.isLoading
    ) {
      if (version.vIdentifier !== remoteVersion) {
        setShowUpdateAlert(true);
      }
    }
  }, [
    isNative,
    forceUpdate,
    isMaintenanceMode,
    version.vIdentifier,
    remoteVersion,
    appQuery.isLoading,
  ]);

  const redirectAuth = ({
    search,
    pathname,
  }: {
    search: any;
    pathname: string;
  }) => {
    const query = deserialize(search);
    const s = serialize(query) ? `?${serialize(query)}` : "";
    const to = {
      pathname: query.pathname
        ? query.pathname
        : ["/auth/register", "/auth/register-cloudbeds"].includes(pathname)
        ? "/ai"
        : "/",
      search: s,
    };
    return to;
  };

  const redirectDash = (search: any, pathname: string) => {
    const s = search
      ? `${search}&pathname=${pathname}`
      : `?pathname=${pathname}`;

    const to = {
      pathname: "/auth/login",
      search: s,
    };
    return to;
  };

  const redirectDoor = (location: Location, logged: boolean) => {
    const { search, pathname } = location;
    const query = deserialize(search);
    switch (pathname) {
      case "/door/invite":
        return {
          pathname: logged
            ? "/account/accept-invitation"
            : "/auth/register-user",
          search,
        };
      case "/door/meta":
        return {
          pathname: logged ? "/ai/channels" : "/auth/login",
          search: serialize({ ...query, provider: "ig" }),
        };
      case "/door/shopify":
        const searchQ = serialize({ ...query, provider: "shop" });
        return {
          pathname: logged ? "/ai/integrations" : "/auth/login",
          search: `${searchQ}&pathname=/ai/integrations`,
        };
      case "/door/stripe":
        return {
          pathname: logged ? "/payments/stripe" : "/auth/login",
          search: search
            ? `${search}&pathname=/payments/stripe`
            : "?pathname=/payments/stripe",
        };
      case "/door":
      default:
        return {
          pathname: logged ? "/ai/integrations" : "/auth/register-cloudbeds",
          search: serialize({ ...query, provider: "cb" }),
        };
    }
  };

  if (loading || appQuery.isLoading) return null;

  const app = (
    <IonApp>
      <PushNotificationProvider isAuthenticated={user.logged}>
        <NotificationProvider isAuthenticated={user.logged}>
          <SocketProvider isAuthenticated={user.logged}>
            <IntlProvider locale={language} messages={messages}>
              <IonReactRouter>
                <GoogleAnalytics />
                {isMaintenance || showUpdateAlert ? (
                  <Switch>
                    <Route
                      render={() => (
                        <Maintenance
                          updateApp={showUpdateAlert}
                          remoteVersion={remoteVersion}
                        />
                      )}
                    />
                  </Switch>
                ) : (
                  <Switch>
                    <Route exact path="/web-chat" component={WebChat} />
                    <Route
                      path="/auth"
                      render={(props) => {
                        const { search, pathname } = props.location;
                        const to = redirectAuth({ search, pathname });
                        return user.logged ? <Redirect to={to} /> : <HOCAuth />;
                      }}
                    />
                    <Route
                      path="/door"
                      render={(props) => {
                        const to = redirectDoor(props.location, user.logged);
                        return <Redirect to={to} />;
                      }}
                    />
                    <Route
                      path="/"
                      render={(props) => {
                        const { search, pathname } = props.location;
                        const to = redirectDash(search, pathname);
                        return user.logged ? (
                          <HOCDashboard refreshUser={refreshUser} {...props} />
                        ) : (
                          <Redirect to={to} />
                        );
                      }}
                    />
                  </Switch>
                )}
              </IonReactRouter>
            </IntlProvider>
          </SocketProvider>
        </NotificationProvider>
      </PushNotificationProvider>
    </IonApp>
  );

  return app;
};

export default App;
